import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { clicksService, type ClicksMark } from "@/services/ClicksService";

const marksKey = (field: string | undefined) => ["clicks", "marks", field] as const;

/** PGRST202: get_clicks_marks is not on this database yet (20261030183128). */
const notDeployed = (error: unknown): boolean => (error as { code?: string } | null)?.code === "PGRST202";

/**
 * One field's row marks, value → mark. `available` once they have loaded: not
 * while loading, not before the marks migration is deployed (PGRST202) - the
 * table then shows no mark buttons rather than buttons that fail.
 */
export function useClicksMarks(field: string | undefined) {
  const query = useQuery({
    queryKey: marksKey(field),
    queryFn: () => clicksService.getMarks(field!),
    enabled: Boolean(field),
    staleTime: 60_000,
    retry: (n, error) => !notDeployed(error) && n < 1,
    throwOnError: false,
    select: (list) => new Map(list.map((m) => [m.value, m])),
  });
  return { marks: query.data, available: Boolean(field) && query.isSuccess };
}

/**
 * Set or remove a mark. Shown at once (optimistic), rolled back on an error.
 * With a mark filter on, the reports read the marks too, so they refresh.
 */
export function useSetClicksMark(field: string | undefined, { reportsReadMarks }: { reportsReadMarks: boolean }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ value, mark }: { value: string; mark: string | null }) => clicksService.setMark(field!, value, mark),
    onMutate: async ({ value, mark }) => {
      await qc.cancelQueries({ queryKey: marksKey(field) });
      const before = qc.getQueryData<ClicksMark[]>(marksKey(field));
      qc.setQueryData<ClicksMark[]>(marksKey(field), (cur = []) => [
        ...(mark ? [{ value, mark, by: null, at: new Date().toISOString() }] : []),
        ...cur.filter((m) => m.value !== value),
      ]);
      return { before };
    },
    onError: (error, _vars, ctx) => {
      qc.setQueryData(marksKey(field), ctx?.before);
      toast.error("Could not save the mark", { description: error instanceof Error ? error.message : undefined });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: marksKey(field) });
      if (reportsReadMarks)
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "clicks" && q.queryKey[1] !== "marks" });
    },
  });
}
